import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import UnifiedModal from '../common/UnifiedModal';
import { useAppDispatch } from '../../../store/hooks';
import { createBulkLessons } from '../../../store/slices/lessonSlice';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const Label = styled.label`
  font-weight: 600;
  font-size: 0.9rem;
  color: var(--text-dark, #374151);
`;

const Input = styled.input`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-light, #e5e7eb);
  background: var(--input-bg, #fff);
  color: var(--text, #111827);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue, #667eea);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const Select = styled.select`
  padding: 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--border-light, #e5e7eb);
  background: var(--input-bg, #fff);
  color: var(--text, #111827);
  font-size: 1rem;

  &:focus {
    outline: none;
    border-color: var(--primary-blue, #667eea);
    box-shadow: 0 0 0 2px rgba(102, 126, 234, 0.1);
  }
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  border: none;

  ${({ $variant }) =>
    $variant === 'secondary'
      ? `
    background: var(--border-light, #e5e7eb);
    color: var(--text-dark, #374151);
    &:hover { background: #d1d5db; }
  `
      : `
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    &:hover { filter: brightness(1.1); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
  `}
`;

const ErrorMsg = styled.div`
  color: #ef4444;
  font-size: 0.85rem;
  margin-top: 0.25rem;
`;

interface BulkLessonCreateModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 0.5rem;
  justify-content: flex-end;

  @media (max-width: 768px) {
    flex-direction: column-reverse;
    
    button {
      width: 100%;
    }
  }
`;

const BulkLessonCreateModal: React.FC<BulkLessonCreateModalProps> = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Default values: start from the next day at 09:00
  const now = new Date();
  const nextDay = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 9, 0);
  
  const formatDateTimeLocal = (date: Date) => {
    const pad = (n: number) => n.toString().padStart(2, '0');
    const y = date.getFullYear();
    const m = pad(date.getMonth() + 1);
    const d = pad(date.getDate());
    const h = pad(date.getHours());
    const min = pad(date.getMinutes());
    return `${y}-${m}-${d}T${h}:${min}`;
  };

  const defaultStart = formatDateTimeLocal(nextDay);
  // Default end: 8 hours after start
  const defaultEnd = formatDateTimeLocal(new Date(nextDay.getTime() + 8 * 60 * 60 * 1000));

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [intervalHours, setIntervalHours] = useState(2);
  const [durationMinutes, setDurationMinutes] = useState(60);
  const [lessonType, setLessonType] = useState('REGULAR');
  const [examType, setExamType] = useState('NORMAL');
  const [licenseType, setLicenseType] = useState('B');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (new Date(startDate) >= new Date(endDate)) {
      setError('Start date must be before end date');
      return;
    }

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(0, 0, 0, 0);

    if (new Date(startDate) < tomorrow) {
      setError('Bulk lessons must start from tomorrow onward');
      return;
    }

    setLoading(true);
    try {
      await dispatch(createBulkLessons({
        startDate: startDate + ':00',
        endDate: endDate + ':00',
        intervalHours,
        durationMinutes,
        lessonType,
        examType: lessonType === 'EXAM' ? examType : undefined,
        licenseType: lessonType === 'EXAM' ? licenseType : undefined
      })).unwrap();
      
      setLoading(false);
      onSuccess?.();
      onClose();
    } catch (err: any) {
      setLoading(false);
      const backendMessage = err?.message || (typeof err === 'string' ? err : null);
      setError(backendMessage || t('portal.lessons.bulk_create.error'));
    }
  };

  return (
    <UnifiedModal
      open={open}
      onClose={onClose}
      title={String(t('portal.lessons.bulk_create.title'))}
      variant="center"
      size="md"
    >
      <Form onSubmit={handleSubmit}>
        {error && <ErrorMsg>{error}</ErrorMsg>}
        
        <FormGroup>
          <Label htmlFor="startDate">{t('portal.lessons.bulk_create.start_date')}</Label>
          <Input
            id="startDate"
            type="datetime-local"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="endDate">{t('portal.lessons.bulk_create.end_date')}</Label>
          <Input
            id="endDate"
            type="datetime-local"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="interval">{t('portal.lessons.bulk_create.interval')}</Label>
          <Input
            id="interval"
            type="number"
            min="1"
            max="24"
            value={intervalHours}
            onChange={(e) => setIntervalHours(parseInt(e.target.value))}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="duration">{t('portal.lessons.bulk_create.duration')}</Label>
          <Input
            id="duration"
            type="number"
            min="15"
            max="480"
            step="15"
            value={durationMinutes}
            onChange={(e) => setDurationMinutes(parseInt(e.target.value))}
            required
          />
        </FormGroup>

        <FormGroup>
          <Label htmlFor="lessonType">{t('portal.lessons.bulk_create.lesson_type', 'Lesson Type')}</Label>
          <Select
            id="lessonType"
            value={lessonType}
            onChange={(e) => setLessonType(e.target.value)}
            required
          >
            <option value="REGULAR">{t('portal.lesson.type.regular', 'Regular')}</option>
            <option value="PRACTICAL">{t('portal.lesson.type.practical', 'Practical')}</option>
            <option value="EXAM_PREP">{t('portal.lesson.type.exam_prep', 'Exam Preparation')}</option>
            <option value="EXAM">{t('portal.lesson.type.exam', 'Exam')}</option>
          </Select>
        </FormGroup>

        {lessonType === 'EXAM' && (
          <>
            <FormGroup>
              <Label htmlFor="examType">{t('portal.lessons.bulk_create.exam_type', 'Exam Type')}</Label>
              <Select
                id="examType"
                value={examType}
                onChange={(e) => setExamType(e.target.value)}
                required
              >
                <option value="NORMAL">{t('portal.exam.type.normal', 'Normal')}</option>
                <option value="BNOR">{t('portal.exam.type.bnor', 'BNOR')}</option>
                <option value="STRESS_MANAGED">{t('portal.exam.type.stress_managed', 'Stress Managed')}</option>
              </Select>
            </FormGroup>

            <FormGroup>
              <Label htmlFor="licenseType">{t('portal.lessons.bulk_create.license_type', 'License Type')}</Label>
              <Select
                id="licenseType"
                value={licenseType}
                onChange={(e) => setLicenseType(e.target.value)}
                required
              >
                <option value="B">B</option>
                <option value="BE">BE</option>
                <option value="A">A</option>
                <option value="AM">AM</option>
              </Select>
            </FormGroup>
          </>
        )}

        <ButtonGroup>
          <Button type="button" $variant="secondary" onClick={onClose}>
            {t('modal.cancel')}
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? t('portal.common.loading') : t('portal.lessons.bulk_create.submit')}
          </Button>
        </ButtonGroup>
      </Form>
    </UnifiedModal>
  );
};

export default BulkLessonCreateModal;
